import { useAuth, UserRole } from '../context/AuthContext';
import { Navigate, useLocation } from 'react-router';
import { RoleGuard } from './RoleGuard';
import { LandingPage } from '../pages/public';

interface ProtectedRouteProps {
  allowedRoles?: UserRole[];
  children: React.ReactNode;
}

/**
 * ProtectedRoute - Wraps pages with auth + role checks.
 * If no allowedRoles are provided, only checks authentication.
 *
 * Special case: unauthenticated users at "/" see the LandingPage
 * instead of being redirected to /login.
 */
export function ProtectedRoute({ allowedRoles, children }: ProtectedRouteProps) {
  const { user, isAuthLoading } = useAuth();
  const location = useLocation();

  if (isAuthLoading) {
    return null;
  }

  // Auth check
  if (!user) {
    // Root path shows the public landing page
    if (location.pathname === '/') {
      return <LandingPage />;
    }
    return <Navigate to="/login" replace />;
  }

  // Role check (optional)
  if (allowedRoles) {
    return <RoleGuard allowedRoles={allowedRoles}>{children}</RoleGuard>;
  }

  return <>{children}</>;
}
